import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ProductTemplate, ProductTemplateCreate } from '../../entity/api/product-template';
import {
  createProductTemplate,
  deleteProductTemplate,
  getProductTemplateById,
  getProductTemplatesByBrandId,
  getProductTemplatesList,
  updateProductTemplate,
  usedByProducts,
} from '../../sdk/product-template';
import { getQueryClient } from '../../sdk/request';

// Query Keys
export const productTemplateQueryKeys = {
  all: ['product-templates'] as const,
  lists: function () {
    return [...productTemplateQueryKeys.all, 'list'] as const;
  },
  list: function () {
    return [...productTemplateQueryKeys.lists()] as const;
  },
  details: function () {
    return [...productTemplateQueryKeys.all, 'detail'] as const;
  },
  detail: function (id: string) {
    return [...productTemplateQueryKeys.details(), id] as const;
  },
  byBrand: function (brandId: string) {
    return [...productTemplateQueryKeys.all, 'by-brand', brandId] as const;
  },
  usedByProducts: function (id: string) {
    return [...productTemplateQueryKeys.all, 'used-by-products', id] as const;
  },
} as const;

// Query: Get All Product Templates
export function useProductTemplatesList() {
  return useQuery({
    queryKey: productTemplateQueryKeys.list(),
    queryFn: async function () {
      const response = await getProductTemplatesList();
      return response.data.productTemplates;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Query: Get Product Template by ID
export function useProductTemplateById(id: string | undefined) {
  return useQuery({
    queryKey: productTemplateQueryKeys.detail(id ?? ''),
    queryFn: async function () {
      if (id == null) {
        throw new Error('Product template ID is required');
      }
      const response = await getProductTemplateById(id);
      return response.data;
    },
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Query: Get Product Templates by Brand ID
export function useProductTemplatesByBrandId(brandId: string | undefined) {
  return useQuery({
    queryKey: productTemplateQueryKeys.byBrand(brandId ?? ''),
    queryFn: async function () {
      if (brandId == null) {
        throw new Error('Brand ID is required');
      }
      const response = await getProductTemplatesByBrandId(brandId);
      return response.data.productTemplates;
    },
    enabled: Boolean(brandId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Query: Check if Product Template is Used by Products
export function useProductTemplateUsedByProducts(id: string | undefined) {
  return useQuery({
    queryKey: productTemplateQueryKeys.usedByProducts(id ?? ''),
    queryFn: async function () {
      if (id == null) {
        throw new Error('Product template ID is required');
      }
      const response = await usedByProducts(id);
      return response.data;
    },
    enabled: Boolean(id),
    staleTime: 30 * 1000, // 30 seconds - this can change more frequently
    gcTime: 60 * 1000, // 1 minute
  });
}

// Mutation: Create Product Template
export function useCreateProductTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (data: ProductTemplateCreate) {
      const response = await createProductTemplate(data);
      return response.data;
    },
    onSuccess: function (newTemplate) {
      // Invalidate list queries to refetch
      void queryClient.invalidateQueries({
        queryKey: productTemplateQueryKeys.lists(),
      });

      // Invalidate brand-specific queries
      void queryClient.invalidateQueries({
        queryKey: productTemplateQueryKeys.byBrand(newTemplate.brandId),
      });

      // Optionally set the new template in cache
      queryClient.setQueryData(productTemplateQueryKeys.detail(newTemplate._id), newTemplate);
    },
  });
}

// Mutation: Update Product Template
export function useUpdateProductTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (data: Partial<ProductTemplate> & { _id: string }) {
      const response = await updateProductTemplate(data);
      return response.ok;
    },
    onMutate: async function (updatedTemplate) {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: productTemplateQueryKeys.detail(updatedTemplate._id),
      });

      // Snapshot the previous value
      const previousTemplate = queryClient.getQueryData<ProductTemplate>(
        productTemplateQueryKeys.detail(updatedTemplate._id)
      );

      // Optimistically update the cache
      if (previousTemplate != null) {
        queryClient.setQueryData(productTemplateQueryKeys.detail(updatedTemplate._id), {
          ...previousTemplate,
          ...updatedTemplate,
        });
      }

      return { previousTemplate };
    },
    onError: function (_err, updatedTemplate, context) {
      // Rollback on error
      if (context?.previousTemplate != null) {
        queryClient.setQueryData(
          productTemplateQueryKeys.detail(updatedTemplate._id),
          context.previousTemplate
        );
      }
    },
    onSettled: function (_data, _error, updatedTemplate) {
      // Invalidate to refetch
      void queryClient.invalidateQueries({
        queryKey: productTemplateQueryKeys.detail(updatedTemplate._id),
      });
      void queryClient.invalidateQueries({
        queryKey: productTemplateQueryKeys.lists(),
      });
    },
  });
}

// Mutation: Delete Product Template
export function useDeleteProductTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (id: string) {
      await deleteProductTemplate(id);
      return id;
    },
    onSuccess: function (deletedId) {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: productTemplateQueryKeys.detail(deletedId),
      });

      // Invalidate list queries
      void queryClient.invalidateQueries({
        queryKey: productTemplateQueryKeys.lists(),
      });

      // Invalidate all brand queries since we don't know which brand it belonged to
      void queryClient.invalidateQueries({
        queryKey: [...productTemplateQueryKeys.all, 'by-brand'],
      });
    },
  });
}

// Helper: Get product templates list from cache (imperative access)
export function getProductTemplatesListFromCache(): ProductTemplate[] | undefined {
  const queryClient = getQueryClient();
  return queryClient.getQueryData<ProductTemplate[]>(productTemplateQueryKeys.list());
}

// Helper: Get product template by ID from cache (imperative access)
export function getProductTemplateByIdFromCache(id: string): ProductTemplate | undefined {
  const queryClient = getQueryClient();
  return queryClient.getQueryData<ProductTemplate>(productTemplateQueryKeys.detail(id));
}

// Helper: Get product templates by brand ID from cache (imperative access)
export function getProductTemplatesByBrandIdFromCache(
  brandId: string
): ProductTemplate[] | undefined {
  const queryClient = getQueryClient();
  return queryClient.getQueryData<ProductTemplate[]>(productTemplateQueryKeys.byBrand(brandId));
}

// Helper: Prefetch product templates list
export async function prefetchProductTemplatesList(): Promise<void> {
  const queryClient = getQueryClient();

  await queryClient.prefetchQuery({
    queryKey: productTemplateQueryKeys.list(),
    queryFn: async function () {
      const response = await getProductTemplatesList();
      return response.data.productTemplates;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Helper: Prefetch product template by ID
export async function prefetchProductTemplateById(id: string): Promise<void> {
  const queryClient = getQueryClient();

  await queryClient.prefetchQuery({
    queryKey: productTemplateQueryKeys.detail(id),
    queryFn: async function () {
      const response = await getProductTemplateById(id);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Helper: Prefetch product templates by brand ID
export async function prefetchProductTemplatesByBrandId(brandId: string): Promise<void> {
  const queryClient = getQueryClient();

  await queryClient.prefetchQuery({
    queryKey: productTemplateQueryKeys.byBrand(brandId),
    queryFn: async function () {
      const response = await getProductTemplatesByBrandId(brandId);
      return response.data.productTemplates;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Helper: Invalidate all product template queries
export function invalidateProductTemplates(): void {
  const queryClient = getQueryClient();
  void queryClient.invalidateQueries({ queryKey: productTemplateQueryKeys.all });
}

// Helper: Invalidate product template list queries
export function invalidateProductTemplatesList(): void {
  const queryClient = getQueryClient();
  void queryClient.invalidateQueries({ queryKey: productTemplateQueryKeys.lists() });
}

// Helper: Invalidate product template by ID
export function invalidateProductTemplateById(id: string): void {
  const queryClient = getQueryClient();
  void queryClient.invalidateQueries({ queryKey: productTemplateQueryKeys.detail(id) });
}

// Helper: Invalidate product templates by brand ID
export function invalidateProductTemplatesByBrandId(brandId: string): void {
  const queryClient = getQueryClient();
  void queryClient.invalidateQueries({ queryKey: productTemplateQueryKeys.byBrand(brandId) });
}

// Helper: Set product template in cache (useful after mutations)
export function setProductTemplateInCache(template: ProductTemplate): void {
  const queryClient = getQueryClient();
  queryClient.setQueryData(productTemplateQueryKeys.detail(template._id), template);
}

// Helper: Update product template in list cache
export function updateProductTemplateInListCache(template: ProductTemplate): void {
  const queryClient = getQueryClient();

  // Update in main list
  queryClient.setQueryData<ProductTemplate[]>(productTemplateQueryKeys.list(), function (oldData) {
    if (oldData == null) {
      return [template];
    }
    const index = oldData.findIndex(function (t) {
      return t._id === template._id;
    });
    if (index === -1) {
      return [...oldData, template];
    }
    const newData = [...oldData];
    newData[index] = template;
    return newData;
  });

  // Update in brand-specific list
  queryClient.setQueryData<ProductTemplate[]>(
    productTemplateQueryKeys.byBrand(template.brandId),
    function (oldData) {
      if (oldData == null) {
        return [template];
      }
      const index = oldData.findIndex(function (t) {
        return t._id === template._id;
      });
      if (index === -1) {
        return [...oldData, template];
      }
      const newData = [...oldData];
      newData[index] = template;
      return newData;
    }
  );
}

// Helper: Remove product template from list cache
export function removeProductTemplateFromListCache(id: string): void {
  const queryClient = getQueryClient();

  // Remove from main list
  queryClient.setQueryData<ProductTemplate[]>(productTemplateQueryKeys.list(), function (oldData) {
    if (oldData == null) {
      return [];
    }
    return oldData.filter(function (t) {
      return t._id !== id;
    });
  });

  // Remove detail cache
  queryClient.removeQueries({
    queryKey: productTemplateQueryKeys.detail(id),
  });
}
